/**
 * Snowplow events and Katal logs for the macOS grant walkthrough.
 *
 * The hire has already consented, so these answer whether the operating system let them follow
 * through. Both sinks are person-attributed, so neither is the anonymous half of the pair.
 */
import { trackStructEvent } from "@snowplow/browser-tracker";

import { Logger } from "@/apis/Logger";
import {
  type CapabilityBlockReason,
  type PermissionGrantSurface,
  type ReportCause,
} from "@/types/agentcore";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

const category = Day1EventCategory.MacOsPermissions;

/*
 * Mapped rather than sent raw, since the domain ids are lower-kebab and every other label is not.
 * Exhaustive by type: a new grant or cause fails to compile until it has one.
 */
const GRANT_LABELS: Record<CapabilityBlockReason, Day1EventLabel> = {
  "accessibility-not-granted": Day1EventLabel.MacGrantAccessibility,
  "screen-recording-not-granted": Day1EventLabel.MacGrantScreenRecording,
};

const CAUSE_LABELS: Record<ReportCause, Day1EventLabel> = {
  "app-missing": Day1EventLabel.MacGrantCauseAppMissing,
  other: Day1EventLabel.MacGrantCauseOther,
  "status-stale": Day1EventLabel.MacGrantCauseStatusStale,
  "toggle-reverts": Day1EventLabel.MacGrantCauseToggleReverts,
};

/** Which grants are outstanding, as one value a query can group by. The count is on `value`. */
const outstandingLabel = (
  missingGrants: readonly CapabilityBlockReason[]
): Day1EventLabel | undefined => {
  if (missingGrants.length > 1) {
    return Day1EventLabel.MacGrantsBothOutstanding;
  }
  return missingGrants[0] === undefined
    ? undefined
    : GRANT_LABELS[missingGrants[0]];
};

/**
 * What the first probe found, once per load.
 *
 * The denominator for everything else: without it a drop-off cannot be told apart from a hire who
 * already held both grants and never saw the page.
 */
export const emitGrantsProbed = (
  missingGrants: readonly CapabilityBlockReason[],
  didProbeAnswer: boolean
): void => {
  /*
   * An unanswered probe must never be scored as "both grants held". It resolves to nothing missing
   * so the hire is not stranded, but reporting that as a clean pass would hide a broken bridge.
   */
  if (!didProbeAnswer) {
    trackStructEvent({
      category,
      action: Day1EventAction.MacGrantProbeUnanswered,
    });
    void Logger.warn("macOS grant probe gave no answer", undefined, {
      metricType: "MacGrantProbeUnanswered",
    });
    return;
  }

  trackStructEvent({
    category,
    action:
      missingGrants.length === 0
        ? Day1EventAction.MacGrantsHeld
        : Day1EventAction.MacGrantsOutstanding,
    /* Which grants are still missing, so the cases can be told apart in the funnel. */
    label: outstandingLabel(missingGrants),
    value: missingGrants.length,
  });
};

/**
 * The hire reached a grant surface the host reported.
 *
 * @param surface Which one, so the funnel can separate a Settings pane from macOS's own consent
 * dialog. A host too old to say answers `pane`, since its boolean only ever meant that.
 */
export const emitSettingsOpened = (
  reasonId: CapabilityBlockReason,
  surface: PermissionGrantSurface
): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.MacGrantSettingsOpened,
    label: GRANT_LABELS[reasonId],
    property: surface,
  });
};

/**
 * The pane never opened, so the hire was left with a button that appeared to do nothing.
 *
 * Warned rather than counted quietly: the walkthrough's one instruction cannot be followed.
 */
export const emitSettingsOpenFailed = (
  reasonId: CapabilityBlockReason
): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.MacGrantSettingsOpenFailed,
    label: GRANT_LABELS[reasonId],
  });

  void Logger.warn("macOS permission pane did not open", undefined, {
    metricType: "MacGrantSettingsOpenFailed",
  });
};

/** The report dialog was opened, whether or not it was submitted. */
export const emitPermissionsReportOpened = (): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.MacGrantReportOpened,
  });
};

/**
 * A cause was reported, so the assistant is given up on and setup continues manually.
 *
 * Warned, not info: macOS refused a consenting hire, which is a fault rather than a preference.
 */
export const emitPermissionsReportSubmitted = (cause: ReportCause): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.MacGrantReportSubmitted,
    label: CAUSE_LABELS[cause],
  });

  void Logger.warn("macOS grant reported as unobtainable", undefined, {
    metricType: "MacGrantReportSubmitted",
    cause,
  });
};

/**
 * Grants that were outstanding on arrival are now held.
 *
 * The conversion the page exists for, and it cannot be inferred from the arrival counts: a hire who
 * arrives with both held and one who fixes both look identical in {@link emitGrantsProbed}.
 *
 * @param attempts How many times the hire was sent to System Settings before it took.
 */
export const emitGrantsResolved = (attempts: number): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.MacGrantsResolved,
    value: attempts,
  });

  void Logger.info("macOS grants resolved after the walkthrough", undefined, {
    metricType: "MacGrantsResolved",
    attempts: String(attempts),
  });
};

/** The hire went on with the assistant, both grants held. */
export const emitGrantsContinued = (): void => {
  trackStructEvent({ category, action: Day1EventAction.MacGrantContinued });
};

/**
 * A cause was chosen, but the grants landed before the report was sent.
 *
 * Kept off both the refusal and the dismissal counts: the hire answered, so the cause is real, but
 * they are no longer refusing anything and must not be routed to manual setup.
 */
export const emitPermissionsReportSuperseded = (cause: ReportCause): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.MacGrantReportSuperseded,
    label: CAUSE_LABELS[cause],
  });
};

/** The report dialog was dismissed without a cause, so the hire is still trying. */
export const emitPermissionsReportCancelled = (): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.MacGrantReportCancelled,
  });
};

/** The hire started the manual setup, giving up on the assistant for this session. */
export const emitManualSetupBegun = (): void => {
  trackStructEvent({ category, action: Day1EventAction.MacGrantManualBegun });
};

/**
 * A hire who reported on an earlier load was sent straight to the steps.
 *
 * Separate from {@link emitManualSetupBegun} on purpose: that one counts entering manual setup, and
 * a hire already counted when they filed the report must not be counted again on every reload.
 */
export const emitForwardedAfterReport = (): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.MacGrantForwardedAfterReport,
  });
};
